import { DateTime } from 'luxon'

import { PagedResult } from '../DTOs/Common/PagedResult'
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO'
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { StoppageSubTypeDto } from '../DTOs/Stoppage/StoppageSubTypeDto'
import { StoppageTypeDto } from '../DTOs/Stoppage/StoppageTypeDto'
import { queryClient } from '../queryClient'
import { setProvisionalShift } from '../state/reducers/shiftReducer'
import { store } from '../state/store'
import type { DoubleShift } from '../types/Shift'
import { appendCachedStoppage } from './patchCachedStoppages'
import { computeShiftId } from './computeShiftId'
import { diggingPatternMutations } from './diggingPatternMutations'
import { nextTempId } from './tempId'
import { nextShiftAfter, shiftStart } from './shiftBands'

const OPERATIONS_KEY = ['dataTable', 'operations', 'stoppageCalcs'] as const
const DIGGER_LOCATIONS_KEY = ['dataTable', 'chatree', 'diggerLocations'] as const

// Note: option caches whose contents do not depend on the shift, only their cache key does, so they
// can be copied straight across. Some are keyed by date and shift, some by date alone.
const COPYABLE_OPTION_KEYS = [
  ['chatree', 'plants'],
  ['production', 'plants'],
  ['production', 'locationOptions'],
  ['references', 'blastmasterIds'],
  ['geology', 'rehandleTrucks'],
  ['geology', 'rehandleFromLocations'],
  ['geology', 'rehandleToLocations'],
  ['geology', 'crushingFromLocations'],
  ['geology', 'crushingToLocations'],
  ['geology', 'productionTrucks'],
] as const

// Note: these two are NOT copyable. Their options are the shift's operations, so copying the
// completed shift's list would attribute the new shift's rehandle and crushing entries to the old
// shift's operations - which is how "Could not find the shift data to update" happens. They are
// rebuilt from the provisional operations instead, and the sync resolves those temp ids.
const OPERATION_OPTION_KEYS = [
  ['geology', 'rehandleDiggers'],
  ['geology', 'crushingLoaders'],
] as const

// Note: the new shift genuinely has no rows yet, so its lists are seeded empty. Without this the
// pages have no cache entry at all offline and report that they are unavailable rather than empty.
const EMPTY_LIST_KEYS = [
  (d: string, s: DoubleShift) => ['dataTable', 'stoppages', 'list', d, s, 'Down & Up', null, '', null, 'all', false, null],
  (d: string, s: DoubleShift) => ['dataTable', 'geology', 'rehandles', d, s, 'all', false, null],
  (d: string, s: DoubleShift) => ['dataTable', 'geology', 'crushing', d, s, 'all', false, null],
  (d: string, s: DoubleShift) => ['dataTable', 'chatree', 'diggingLoadingSummary', d, s, 'all', false, null],
] as const

// Note: how early a completion may be clicked. Completing offline invents the next shift locally,
// so a mistaken click hours ahead of time would create a shift the crew then enters data against.
export const EARLIEST_COMPLETION_MINUTES_BEFORE_START = 45

export interface ProvisionalShiftResult {
  date: string
  shift: DoubleShift
  shiftId: number
  /** Machines carried into the new shift. Zero means nothing was cached for the completed shift. */
  operationsCarried: number
  patternsCarried: number
}

function isEmptyList(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (Array.isArray(value)) return value.length === 0
  const rows = (value as { data?: unknown }).data
  return Array.isArray(rows) && rows.length === 0
}

// Note: never overwrite rows already in the cache. Provisional rows are guesses; a shift that has
// real cached data (e.g. the next shift already existed on the server) must keep it. An entry that
// holds an *empty* list is not data worth protecting though - a shift visited online before it
// existed server-side caches a zero-row page, and refusing to seed over that left the crew with a
// provisional shift containing no machines at all.
function seedIfEmpty(key: unknown[], value: unknown): boolean {
  if (!isEmptyList(queryClient.getQueryData(key))) return false
  queryClient.setQueryData(key, value)
  return true
}

function retargetKey(key: unknown[], date: string, shift: DoubleShift, dateIndex: number): unknown[] {
  const next = [...key]
  next[dateIndex] = date
  next[dateIndex + 1] = shift
  return next
}

// Note: the client has no local database, so "creating TblOperation rows" means seeding the cache
// the Operations list reads for the new shift: one row per plant that was in the completed shift,
// carrying EndEngineHours -> StartEngineHours and the machine's location. A machine that was still
// down at the end of the completed shift stays down - a breakdown does not end because a shift did.
async function seedOperations(fromDate: string, fromShift: string, to: ProvisionalShiftResult): Promise<number> {
  const sources = queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: [...OPERATIONS_KEY, fromDate, fromShift],
  })
  let carried = 0

  for (const [key, page] of sources) {
    const plants = page?.data ?? []
    if (!plants.length) continue

    const rows: StoppageCalc[] = []
    for (const plant of plants) {
      const stillDown = !!plant.status && plant.status !== 'UP'
      rows.push(new StoppageCalc({
        // Note: allocated from the shared temp-id counter, so the id is negative (never mistaken for
        // a real operation) and small enough for the server's int fields. Deriving it from Date.now()
        // overflowed int and, once such an id reached the queue, poisoned the counter that hands out
        // every other temp id - which failed the whole /Sync request with a 400.
        operationId: await nextTempId(),
        shiftId: to.shiftId,
        plantId: plant.plantId,
        machineName: plant.machineName,
        status: stillDown ? plant.status : 'UP',
        statusDescription: stillDown ? plant.statusDescription : undefined,
        stoppageColourHub: stillDown ? plant.stoppageColourHub : undefined,
        stoppageId: stillDown ? plant.stoppageId : undefined,
        currentStoppageTypeId: stillDown ? plant.currentStoppageTypeId : undefined,
        currentStoppageSubTypeId: stillDown ? plant.currentStoppageSubTypeId : undefined,
        timeIn: stillDown ? plant.timeIn : undefined,
        // Note: the completed shift's end hours become the new shift's start hours, falling back to
        // that shift's start hours where no end reading was entered - engine hours only increase, so
        // the last known reading is the honest figure and a zero reads as a machine that has never
        // run. spCheckCurrentShiftAndCreateNewShiftRecords carries the same fallback, so the value
        // here is the value the completion stores; if the two ever diverge the crew sees a reading
        // before the queue syncs and a different one afterwards.
        startEngineHours: plant.endEngineHours ?? plant.startEngineHours ?? 0,
        locationId: plant.locationId,
      }))
    }

    const seeded = seedIfEmpty(
      retargetKey(key as unknown[], to.date, to.shift, 3),
      new PagedResult(rows, rows.length, Math.max(1, rows.length), 1, 1000),
    )
    if (seeded) carried = Math.max(carried, rows.length)
  }

  return carried
}

function seedShiftScopedOptions(fromDate: string, fromShift: string, to: ProvisionalShiftResult): void {
  for (const prefix of COPYABLE_OPTION_KEYS) {
    // Note: date-and-shift keyed first, then the date-only ones (which still need copying when the
    // new shift falls on the next day).
    for (const [key, value] of queryClient.getQueriesData({ queryKey: [...prefix, fromDate, fromShift] })) {
      if (value === undefined) continue
      seedIfEmpty(retargetKey(key as unknown[], to.date, to.shift, prefix.length), value)
    }
    const dateOnly = queryClient.getQueryData([...prefix, fromDate])
    if (dateOnly !== undefined) seedIfEmpty([...prefix, to.date], dateOnly)
  }

  for (const buildKey of EMPTY_LIST_KEYS) {
    seedIfEmpty(buildKey(to.date, to.shift) as unknown[], PagedResult.empty())
  }
}

// Note: the Stoppages list was seeded empty, so a machine carried into the new shift still down showed
// as down in the Operations list while the maintenance crew's own list said the shift had no stoppages
// at all - four machines down, one row listed. The stoppages carried over belong in that list too,
// under the same maintenance-only rule the endpoint applies.
function seedCarriedStoppages(to: ProvisionalShiftResult): void {
  const carried: StoppageDto[] = []

  for (const [key, page] of queryClient.getQueriesData<PagedResult<StoppageDto>>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', to.date, to.shift],
  })) {
    if (typeof (key as unknown[])[5] !== 'number') continue
    for (const row of page?.data ?? []) {
      if (row?.stoppageId && !carried.some(existing => existing.stoppageId === row.stoppageId)) carried.push(row)
    }
  }

  for (const stoppage of carried) {
    appendCachedStoppage(stoppage, to.date, to.shift)
  }
}

// Note: rebuilds the rehandle-digger and crushing-loader option lists for the new shift by taking
// the completed shift's list and swapping each entry's operationId for the provisional one of the
// same machine. Taking the old list keeps the right membership (only machines that are rehandle
// diggers or crushing loaders, not every plant) and the fields the pages need beyond the operation -
// a crushing loader also carries its bucket, and without it the page rejects the row with "Please
// select a Loader". Entries whose machine has no provisional operation are dropped rather than left
// pointing at the completed shift's operation. geoRehandleMutations records the plant alongside the
// temp id so the sync can swap in the real operation once the completion procedure has created it.
function seedOperationOptions(fromDate: string, fromShift: string, to: ProvisionalShiftResult): void {
  const operations = provisionalOperations(to)
  if (!operations.length) return

  const byPlantId = new Map<number, number>()
  const byPlantRef = new Map<string, number>()
  for (const op of operations) {
    if (op.plantId) byPlantId.set(op.plantId, op.operationId)
    if (op.machineName) byPlantRef.set(op.machineName, op.operationId)
  }

  for (const prefix of OPERATION_OPTION_KEYS) {
    const source = queryClient.getQueryData<Record<string, unknown>[]>([...prefix, fromDate, fromShift])
    if (!Array.isArray(source) || !source.length) continue

    const remapped = source
      .map(option => {
        const plantId = option.plantId as number | undefined
        const plantRef = option.plantRef as string | undefined
        const operationId = (plantId ? byPlantId.get(plantId) : undefined)
          ?? (plantRef ? byPlantRef.get(plantRef) : undefined)
        return operationId ? { ...option, operationId } : null
      })
      .filter((option): option is NonNullable<typeof option> => option !== null)

    if (remapped.length) seedIfEmpty([...prefix, to.date, to.shift], remapped)
  }
}

// Note: the Operations list's nested table fetches per plant, so without an entry for each machine
// every expanded row in the new shift reported itself unavailable offline. A machine carried over
// still down keeps its open stoppage in the list, so the crew can close it in the new shift; the rest
// get an empty list. The key's status segment is the machine's status in the new shift.
function seedNestedStoppages(fromDate: string, fromShift: string, to: ProvisionalShiftResult): void {
  const openByPlant = new Map<number, StoppageDto[]>()
  for (const [key, page] of queryClient.getQueriesData<PagedResult<StoppageDto>>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', fromDate, fromShift],
  })) {
    const plantId = (key as unknown[])[5]
    if (typeof plantId !== 'number') continue
    const open = (page?.data ?? []).filter(row => row?.endTime == null)
    if (open.length) openByPlant.set(plantId, open)
  }

  for (const op of provisionalOperations(to)) {
    if (!op.plantId) continue
    const stillDown = !!op.status && op.status !== 'UP'
    const rows = stillDown
      ? openByPlant.get(op.plantId) ?? [stoppageFromOperation(op)].filter((row): row is StoppageDto => row !== null)
      : []
    seedIfEmpty(
      ['dataTable', 'operations', 'plantStoppages', to.date, to.shift, op.plantId, op.status, 1, 5, null, 'asc', false, null],
      new PagedResult(rows, rows.length, Math.max(1, rows.length), 1, 5),
    )
  }
}

// Note: the open stoppage keeping a machine down, rebuilt from its row in the Operations list. The
// carried-over stoppage is normally copied from the completed shift's own nested cache, but that only
// exists for machines whose row somebody had expanded - so a machine could arrive in the new shift
// marked down with an expanded row reporting "No results found", and no way to close the stoppage. The
// Operations row carries everything the nested table shows, and the real stoppage id with it, so a
// time out entered against this row updates the actual record.
function stoppageFromOperation(op: StoppageCalc): StoppageDto | null {
  if (!op.stoppageId || !op.plantId) return null

  return new StoppageDto({
    stoppageId: op.stoppageId,
    plantId: op.plantId,
    startTime: op.timeIn!,
    stoppageTypeId: op.currentStoppageTypeId ?? 0,
    stoppageSubTypeId: op.currentStoppageSubTypeId ?? 0,
    incident: false,
    stoppageSubType: new StoppageSubTypeDto({
      stoppageSubTypeId: op.currentStoppageSubTypeId ?? 0,
      // Note: the Operations row holds "<type> - <sub-type>"; the nested table shows the sub-type.
      stoppageSubType: (op.statusDescription ?? '').split(' - ').slice(1).join(' - ') || (op.statusDescription ?? ''),
      stoppageTypeId: op.currentStoppageTypeId ?? 0,
      departmentOwnerId: 0,
    }),
    stoppageType: new StoppageTypeDto({
      stoppageTypeId: op.currentStoppageTypeId ?? 0,
      stoppageType: op.status ?? '',
      colourHex: op.stoppageColourHub,
    }),
  })
}

function provisionalOperations(to: ProvisionalShiftResult): StoppageCalc[] {
  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: [...OPERATIONS_KEY, to.date, to.shift],
  })) {
    const rows = page?.data ?? []
    if (rows.length) return rows
  }
  return []
}

// Note: spec item 5 - for every plant that is UP (no open stoppage) copy its most recent digging
// pattern into the new shift, so digger operators have somewhere to post loads immediately. The
// pattern is queued as a real create carrying a negative temp id; the sync applies it after the
// completion (so the shift and its operations exist) and repoints any loads queued against it.
async function carryDiggingPatterns(
  fromDate: string,
  fromShift: string,
  to: ProvisionalShiftResult,
): Promise<number> {
  const upPlantIds = new Set<number>()
  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: [...OPERATIONS_KEY, fromDate, fromShift],
  })) {
    for (const row of page?.data ?? []) {
      if (row?.status === 'UP' && row.plantId) upPlantIds.add(row.plantId)
    }
  }

  const startTime = shiftStart(to.date, to.shift)
  let carried = 0

  for (const [key, page] of queryClient.getQueriesData<PagedResult<DiggingLocationDTO>>({
    queryKey: [...DIGGER_LOCATIONS_KEY, fromDate, fromShift],
  })) {
    const diggerId = (key as unknown[])[5]
    if (typeof diggerId !== 'number' || !upPlantIds.has(diggerId)) continue

    const locations = page?.data ?? []
    if (!locations.length) continue

    const targetKey = retargetKey(key as unknown[], to.date, to.shift, 3)
    if (queryClient.getQueryData(targetKey) !== undefined) continue

    const mostRecent = locations.reduce((latest, candidate) =>
      (candidate.startTime > latest.startTime ? candidate : latest))

    const carriedLocation = new DiggingLocationDTO({ ...mostRecent, startTime, rowVersion: undefined })
    const response = await diggingPatternMutations.insertDiggingLocation(
      DateTime.fromISO(to.date), to.shift, diggerId, carriedLocation, to.shiftId,
    )
    if (!response?.data) continue

    queryClient.setQueryData(targetKey, new PagedResult([response.data], 1, 1, 1, 5))
    carried++
  }

  // Note: the Production list's row for each carried location is added by insertDiggingLocation
  // itself (see patchCachedDiggingSummary), so a location entered by hand offline gets one too.

  return carried
}

// Note: how early the completion is being attempted, in minutes (negative once the next shift has
// started). The caller refuses a completion that is further ahead than the allowed window.
export function minutesBeforeNextShiftStart(fromDate: string, fromShift: string): number {
  const next = nextShiftAfter(fromDate, fromShift)
  return shiftStart(next.date, next.shift).diff(DateTime.now(), 'minutes').minutes
}

// Note: builds the shift the crew moves into when they complete a shift while offline. Only ever
// the next shift (day -> night -> next day's day); a second concurrent shift is not created,
// because a network outage long enough to need one is a bigger problem than this can solve.
export async function createProvisionalNextShift(
  fromDate: string,
  fromShift: string,
): Promise<ProvisionalShiftResult> {
  const next = nextShiftAfter(fromDate, fromShift)
  const result: ProvisionalShiftResult = {
    date: next.date,
    shift: next.shift,
    shiftId: computeShiftId(next.date, next.shift),
    operationsCarried: 0,
    patternsCarried: 0,
  }

  result.operationsCarried = await seedOperations(fromDate, fromShift, result)
  seedShiftScopedOptions(fromDate, fromShift, result)
  seedOperationOptions(fromDate, fromShift, result)
  seedNestedStoppages(fromDate, fromShift, result)
  seedCarriedStoppages(result)
  result.patternsCarried = await carryDiggingPatterns(fromDate, fromShift, result)

  // Note: nothing can report the lock state of a shift that does not exist on the server yet, and
  // offline every shift but the latest reads as locked. Recording it as provisional keeps it
  // editable until the completion syncs.
  store.dispatch(setProvisionalShift({ date: result.date, shift: result.shift }))

  return result
}
